<template>
    <v-card elevation="2">
        <!-- Filters. Each wraps onto its own line on a phone. -->
        <v-row dense class="px-4 pt-4">
            <v-col cols="12" sm="6" md="2">
                <v-select v-model="preset" :items="INSPECTION_PRESETS" label="Dates" density="compact"
                          variant="outlined" hide-details/>
            </v-col>
            <template v-if="preset === 'custom'">
                <v-col cols="6" sm="3" md="2">
                    <v-text-field v-model="customFrom" type="date" label="From" density="compact" variant="outlined"
                                  hide-details :max="customTo || undefined"/>
                </v-col>
                <v-col cols="6" sm="3" md="2">
                    <v-text-field v-model="customTo" type="date" label="To" density="compact" variant="outlined"
                                  hide-details :min="customFrom || undefined" :error="customInvalid"/>
                </v-col>
            </template>
            <v-col cols="12" sm="6" md="3">
                <v-autocomplete v-model="vehicle" :items="vehicles" item-title="label" item-value="id" multiple chips
                                closable-chips label="Vehicle" density="compact" variant="outlined" hide-details
                                clearable :loading="vehiclesLoading" no-data-text="No vehicles"/>
            </v-col>
            <v-col cols="12" sm="6" md="3">
                <v-text-field v-model="search" label="Search" density="compact" variant="outlined" hide-details
                              clearable prepend-inner-icon="mdi-magnify" maxlength="100"
                              placeholder="Driver, vehicle or notes"/>
            </v-col>
            <v-col cols="12" sm="6" md="4" class="d-flex align-center">
                <v-chip-group v-model="status" multiple column aria-label="Result">
                    <v-chip v-for="r in RESULTS" :key="r.value" :value="r.value" size="small" variant="outlined"
                            filter :color="r.color">{{ r.title }}</v-chip>
                </v-chip-group>
            </v-col>
            <v-col cols="12" class="d-flex flex-wrap align-center ga-2">
                <v-btn size="small" variant="text" :prepend-icon="moreOpen ? 'mdi-chevron-up' : 'mdi-tune-variant'"
                       :aria-expanded="moreOpen ? 'true' : 'false'" aria-controls="inspection-more-filters"
                       @click="moreOpen = !moreOpen">
                    More filters<span v-if="moreCount">&nbsp;({{ moreCount }})</span>
                </v-btn>
                <v-spacer/>
                <v-select v-if="compact" v-model="phoneSort" :items="PHONE_SORTS" label="Sort" density="compact"
                          variant="outlined" hide-details style="max-width: 200px"/>
                <v-tooltip location="top" :text="exportTip" :disabled="!exportTip">
                    <template #activator="{props: tip}">
                        <span v-bind="tip">
                            <v-btn size="small" variant="tonal" prepend-icon="mdi-download" :loading="exporting"
                                   :disabled="!!exportBlocked" @click="exportCsv">Export CSV</v-btn>
                        </span>
                    </template>
                </v-tooltip>
            </v-col>
        </v-row>
        <v-expand-transition>
            <v-row v-show="moreOpen" id="inspection-more-filters" dense class="px-4">
                <v-col cols="12" sm="6" md="3">
                    <v-autocomplete v-model="driver" :items="drivers" item-title="name" item-value="id" multiple chips
                                    closable-chips label="Driver" density="compact" variant="outlined" hide-details
                                    clearable :loading="driversLoading" no-data-text="No drivers"/>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                    <v-select v-model="defectStatus" :items="INSPECTION_DEFECT_FILTERS" label="Defect status"
                              density="compact" variant="outlined" hide-details clearable/>
                </v-col>
                <v-col cols="6" sm="6" md="3">
                    <v-select v-model="type" :items="TYPES" label="Type" density="compact" variant="outlined"
                              hide-details clearable/>
                </v-col>
                <v-col cols="6" sm="6" md="3">
                    <v-select v-model="gps" :items="GPS_FILTERS" label="GPS verified" density="compact"
                              variant="outlined" hide-details/>
                </v-col>
            </v-row>
        </v-expand-transition>

        <v-alert v-if="customInvalid" type="warning" variant="tonal" density="compact" class="mx-4 my-2">
            The end date is before the start date.
        </v-alert>

        <!-- Active filters -->
        <div v-if="activeFilters.length" class="d-flex flex-wrap align-center ga-2 px-4 pt-3">
            <v-chip v-for="c in activeFilters" :key="c.key" size="small" closable @click:close="c.clear()">
                {{ c.label }}
            </v-chip>
            <v-btn size="small" variant="text" color="primary" @click="clearAll">Clear all</v-btn>
        </div>

        <!-- Totals for everything the filters match; each one narrows to itself -->
        <div v-if="totals && !loadError && !firstRun" class="d-flex flex-wrap ga-2 px-4 py-3">
            <v-chip size="small" variant="tonal" prepend-icon="mdi-format-list-numbered" @click="showAllResults">
                {{ totals.total }} {{ totals.total === 1 ? 'inspection' : 'inspections' }}
            </v-chip>
            <v-chip v-for="r in RESULTS" :key="r.value" size="small" variant="tonal" :color="r.color"
                    @click="onlyResult(r.value)">
                {{ r.title }}: {{ totals[r.value] }}
            </v-chip>
            <v-chip size="small" variant="tonal" prepend-icon="mdi-map-marker-off" @click="gps = '0'">
                {{ totals.gps_not_verified }} not GPS verified
            </v-chip>
            <v-chip size="small" variant="tonal" color="error" prepend-icon="mdi-alert-circle-outline"
                    @click="emit('show-defects')">
                {{ totals.company_open_defects }} open {{ totals.company_open_defects === 1 ? 'defect' : 'defects' }} fleet-wide
            </v-chip>
        </div>

        <v-alert v-if="loadError" type="error" variant="tonal" density="compact" class="mx-4 my-2">
            {{ loadError }}
            <template #append>
                <v-btn size="small" variant="text" @click="fetchRows">Retry</v-btn>
            </template>
        </v-alert>

        <!-- A fleet with no inspections yet: say where they come from and what to set up -->
        <v-card-text v-if="firstRun" class="pt-2">
            <v-sheet border rounded="lg" class="pa-6 text-center mx-auto" max-width="640">
                <v-icon size="48" color="primary" class="mb-2">mdi-clipboard-check-outline</v-icon>
                <h2 class="text-h6 mb-1">No inspections yet</h2>
                <p class="text-body-2 text-medium-emphasis mb-4">
                    Drivers submit pre-trip and post-trip inspections (DVIRs) in the SimpliGPS Driver app.
                    Each one shows here with its photos, signature and GPS point, and any defect lands on the
                    Defects tab for a repair.
                </p>
                <div class="d-flex flex-wrap justify-center ga-2">
                    <v-btn color="primary" variant="flat" prepend-icon="mdi-format-list-checks"
                           :to="{name: 'fleet/inspection-templates'}">Set up checklist</v-btn>
                    <v-btn variant="tonal" prepend-icon="mdi-account-plus" :to="{name: 'drivers'}">Add a driver</v-btn>
                </div>
            </v-sheet>
        </v-card-text>

        <!-- Phone: one stacked row per inspection -->
        <template v-else-if="compact">
            <v-progress-linear :active="loading" indeterminate color="primary" height="2"/>
            <v-list v-if="rows.length" class="py-0" lines="two">
                <template v-for="ins in rows" :key="ins.id">
                    <v-list-item @click="emit('open', ins.id)">
                        <v-list-item-title class="d-flex align-center ga-2">
                            <span class="text-truncate">{{ ins.asset?.label || '-' }}</span>
                            <v-spacer/>
                            <v-chip v-if="outOfService(ins)" size="x-small" variant="flat" color="error">Out of service</v-chip>
                            <v-chip size="x-small" variant="flat" :color="statusColor(ins.status)">{{ statusLabel(ins.status) }}</v-chip>
                        </v-list-item-title>
                        <v-list-item-subtitle class="mt-1">
                            <div class="text-truncate">{{ driverName(ins.driver) }} &middot; {{ typeLabel(ins.type) }}</div>
                            <div>
                                {{ fmtDateTime(ins.submitted_at, zone) }}
                                <span v-if="ins.open_defects_count" class="text-error">
                                    &middot; {{ ins.open_defects_count }} open
                                </span>
                                <span v-if="!ins.gps_verified"> &middot; not GPS verified</span>
                            </div>
                        </v-list-item-subtitle>
                    </v-list-item>
                    <v-divider/>
                </template>
            </v-list>
            <div v-if="lastPage > 1" class="d-flex justify-center py-3">
                <v-pagination v-model="page" :length="lastPage" :total-visible="4" density="compact"/>
            </div>
        </template>

        <v-data-table-server v-else v-model:page="page" v-model:items-per-page="perPage" v-model:sort-by="sortBy"
                             must-sort :headers="headers" :items="rows" :items-length="totalItems"
                             :loading="loading" :items-per-page-options="[10, 20, 50, 100]" hover
                             :row-props="rowProps" density="compact" @click:row="(e, {item}) => emit('open', item.id)">
            <template #item.submitted_at="{item}">
                <span class="text-no-wrap">{{ fmtDateTime(item.submitted_at, zone) }}</span>
            </template>
            <template #item.vehicle="{item}">{{ item.asset?.label || '-' }}</template>
            <template #item.driver="{item}">{{ driverName(item.driver) }}</template>
            <template #item.type="{item}">{{ typeLabel(item.type) }}</template>
            <template #item.status="{item}">
                <v-chip size="x-small" variant="flat" :color="statusColor(item.status)">{{ statusLabel(item.status) }}</v-chip>
            </template>
            <template #item.gps_verified="{item}">
                <v-icon :color="item.gps_verified ? 'success' : undefined" size="18"
                        :class="{'text-medium-emphasis': !item.gps_verified}"
                        :aria-label="item.gps_verified ? 'GPS verified' : 'Not GPS verified'">
                    {{ item.gps_verified ? 'mdi-map-marker-check' : 'mdi-map-marker-off' }}
                </v-icon>
            </template>
            <template #item.open_defects="{item}">
                <span class="d-inline-flex align-center ga-1 text-no-wrap">
                    <v-chip v-if="item.open_defects_count" size="x-small" color="error" variant="tonal">
                        {{ item.open_defects_count }}
                    </v-chip>
                    <span v-else>-</span>
                    <v-chip v-if="outOfService(item)" size="x-small" color="error" variant="flat"
                            prepend-icon="mdi-car-off">Out of service</v-chip>
                </span>
            </template>
            <template #no-data>
                <div class="pa-6 text-center text-medium-emphasis">
                    <div>{{ emptyText }}</div>
                    <v-btn v-if="activeFilters.length" class="mt-2" size="small" variant="tonal" @click="clearAll">
                        Clear filters
                    </v-btn>
                </div>
            </template>
        </v-data-table-server>

        <div v-if="compact && !firstRun && !rows.length && !loading && !loadError" class="pa-6 text-center text-medium-emphasis">
            <div>{{ emptyText }}</div>
            <v-btn v-if="activeFilters.length" class="mt-2" size="small" variant="tonal" @click="clearAll">
                Clear filters
            </v-btn>
        </div>
    </v-card>
</template>

<script setup>
import {computed, nextTick, onBeforeUnmount, onMounted, ref, watch} from "vue";
import {useRoute, useRouter} from "vue-router";
import {useDisplay} from "vuetify";
import {send} from "@/composables/apiClient";
import {companyTimezone} from "@/composables/useTimezone";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {useReportExport} from "@/composables/useReportExport";
import {apiError, presetRange, rangePhrase} from "@/components/drivers/shifts/shiftFormat";
import {
    driverName, driverOptions, fmtDateTime, GPS_FILTERS, ids, INSPECTION_DEFECT_FILTERS, INSPECTION_PRESETS, many,
    one, pageOf, patchQuery, pick, pickPreset, RESULTS, sameQuery, statusColor, statusLabel, titleOf, TYPES,
    typeLabel, vehicleOptions, ymdOr,
} from "./inspectionFormat";

const emit = defineEmits(["open", "totals", "show-defects"]);

const route = useRoute();
const router = useRouter();
const {showSnackbar} = useSnackbarStore();
const {handleDownloadResponse} = useReportExport();
const {xs} = useDisplay();
const compact = computed(() => xs.value);

const SORTS = ["submitted_at", "type", "status", "gps_verified", "open_defects", "vehicle", "driver"];
const headers = [
    {title: "Date", key: "submitted_at"},
    {title: "Vehicle", key: "vehicle"},
    {title: "Driver", key: "driver"},
    {title: "Type", key: "type", align: "center"},
    {title: "Status", key: "status", align: "center"},
    {title: "GPS verified", key: "gps_verified", align: "center"},
    {title: "Open defects", key: "open_defects", align: "center"},
];
const PHONE_SORTS = [
    {value: "submitted_at:desc", title: "Newest first"},
    {value: "submitted_at:asc", title: "Oldest first"},
    {value: "vehicle:asc", title: "Vehicle"},
    {value: "driver:asc", title: "Driver"},
    {value: "open_defects:desc", title: "Most open defects"},
];

// ---- state, read from the URL -------------------------------------------------

const preset = ref("all");
const customFrom = ref("");
const customTo = ref("");
const vehicle = ref([]);
const driver = ref([]);
const status = ref([]);
const defectStatus = ref(null);
const type = ref(null);
const gps = ref("");
const search = ref("");
const searchApplied = ref("");
const sortBy = ref([{key: "submitted_at", order: "desc"}]);
const page = ref(1);
const perPage = ref(20);

const sortKey = computed(() => sortBy.value?.[0]?.key || "submitted_at");
const sortDir = computed(() => (sortBy.value?.[0]?.order === "asc" ? "asc" : "desc"));
const phoneSort = computed({
    get: () => `${sortKey.value}:${sortDir.value}`,
    set: (v) => {
        const [key, order] = v.split(":");
        sortBy.value = [{key, order}];
    },
});

// While the URL is being applied, its page is kept rather than reset.
let applying = false;
const applyQuery = (q) => {
    applying = true;
    preset.value = pickPreset(q.range, "all");
    customFrom.value = ymdOr(q.from);
    customTo.value = ymdOr(q.to);
    vehicle.value = ids(q.vehicle);
    driver.value = ids(q.driver);
    status.value = many(q.status).filter((s) => RESULTS.some((r) => r.value === s));
    defectStatus.value = pick(q.defect_status, INSPECTION_DEFECT_FILTERS.map((d) => d.value), null);
    type.value = pick(q.type, ["pre", "post"], null);
    gps.value = pick(q.gps, ["1", "0"], "");
    search.value = one(q.q).slice(0, 100);
    searchApplied.value = search.value.trim();
    sortBy.value = [{key: pick(q.sort, SORTS, "submitted_at"), order: one(q.dir) === "asc" ? "asc" : "desc"}];
    page.value = pageOf(q.page);
    perPage.value = [10, 20, 50, 100].includes(Number(one(q.per_page))) ? Number(one(q.per_page)) : 20;
    if (moreCount.value) moreOpen.value = true;
    nextTick(() => {
        applying = false;
    });
};

// This list's part of the URL, defaults left out.
const myQuery = () => ({
    range: preset.value === "all" ? "" : preset.value,
    from: preset.value === "custom" ? customFrom.value : "",
    to: preset.value === "custom" ? customTo.value : "",
    vehicle: vehicle.value,
    driver: driver.value,
    status: status.value,
    defect_status: defectStatus.value || "",
    type: type.value || "",
    gps: gps.value,
    q: searchApplied.value,
    sort: sortKey.value === "submitted_at" ? "" : sortKey.value,
    dir: sortDir.value === "desc" ? "" : "asc",
    page: page.value > 1 ? page.value : "",
    per_page: perPage.value === 20 ? "" : perPage.value,
});

// ---- derived --------------------------------------------------------------------

const zone = ref(null);
const tz = computed(() => zone.value || companyTimezone());
const range = computed(() => {
    if (preset.value === "custom") return {from: customFrom.value, to: customTo.value};
    return presetRange(preset.value, tz.value) ?? {from: "", to: ""};
});
const customInvalid = computed(() =>
    preset.value === "custom" && !!customFrom.value && !!customTo.value && customFrom.value > customTo.value);
// "All dates" exports the last 12 months; the API takes at most 366 days.
const exportRange = computed(() => {
    if (preset.value !== "all") return range.value;
    const to = presetRange("today", tz.value)?.to;
    if (!to) return {from: "", to: ""};
    const d = new Date(`${to}T00:00:00Z`);
    d.setUTCFullYear(d.getUTCFullYear() - 1);
    d.setUTCDate(d.getUTCDate() + 1);
    return {from: d.toISOString().slice(0, 10), to};
});
const exportBlocked = computed(() => {
    if (customInvalid.value) return "The end date is before the start date";
    if (!exportRange.value.from || !exportRange.value.to) return "Pick a date range of up to a year to export";
    return "";
});
const exportTip = computed(() => exportBlocked.value
    || (preset.value === "all" ? "Exports the last 12 months. Pick dates to export another range (up to a year)." : ""));

const vehicles = ref([]);
const vehiclesLoading = ref(false);
const drivers = ref([]);
const driversLoading = ref(false);
const vehicleLabel = (id) => vehicles.value.find((v) => v.id === id)?.label || `Vehicle ${id}`;
const driverLabel = (id) => drivers.value.find((d) => d.id === id)?.name || `Driver ${id}`;

const activeFilters = computed(() => {
    const out = [];
    if (preset.value !== "all") {
        const when = rangePhrase(preset.value, range.value.from, range.value.to);
        out.push({key: "range", label: when || titleOf(INSPECTION_PRESETS, preset.value), clear: () => (preset.value = "all")});
    }
    vehicle.value.forEach((id) => out.push({
        key: `v${id}`, label: vehicleLabel(id), clear: () => (vehicle.value = vehicle.value.filter((x) => x !== id)),
    }));
    driver.value.forEach((id) => out.push({
        key: `d${id}`, label: driverLabel(id), clear: () => (driver.value = driver.value.filter((x) => x !== id)),
    }));
    status.value.forEach((s) => out.push({
        key: `s${s}`, label: titleOf(RESULTS, s), clear: () => (status.value = status.value.filter((x) => x !== s)),
    }));
    if (defectStatus.value) {
        out.push({key: "ds", label: `Defects: ${titleOf(INSPECTION_DEFECT_FILTERS, defectStatus.value).toLowerCase()}`, clear: () => (defectStatus.value = null)});
    }
    if (type.value) out.push({key: "type", label: titleOf(TYPES, type.value), clear: () => (type.value = null)});
    if (gps.value) out.push({key: "gps", label: gps.value === "1" ? "GPS verified" : "Not GPS verified", clear: () => (gps.value = "")});
    if (searchApplied.value) out.push({key: "q", label: `"${searchApplied.value}"`, clear: () => (search.value = "")});
    return out;
});

// Driver, defect status, type and GPS sit behind "More filters".
const moreOpen = ref(false);
const moreCount = computed(() => (driver.value.length ? 1 : 0) + (defectStatus.value ? 1 : 0) + (type.value ? 1 : 0) + (gps.value ? 1 : 0));

const emptyText = computed(() => {
    if (loading.value) return "Loading inspections...";
    if (!activeFilters.value.length) return "No inspections yet.";
    return `No inspections match ${activeFilters.value.map((c) => c.label).join(", ")}.`;
});

const clearAll = () => {
    preset.value = "all";
    customFrom.value = "";
    customTo.value = "";
    vehicle.value = [];
    driver.value = [];
    status.value = [];
    defectStatus.value = null;
    type.value = null;
    gps.value = "";
    search.value = "";
    searchApplied.value = "";
};
const onlyResult = (r) => {
    status.value = [r];
};
const showAllResults = () => {
    status.value = [];
};

// The filters as API parameters; the same set drives the list and the export.
// Vehicles with an open critical defect, from the list's totals.
const outOfServiceIds = computed(() => new Set(totals.value?.out_of_service_asset_ids ?? []));
const outOfService = (ins) => outOfServiceIds.value.has(ins.asset_id);

// Table rows open with Enter or Space as well as a click.
const rowProps = ({item}) => ({
    tabindex: 0,
    "aria-label": `Open inspection: ${item.asset?.label || "vehicle"}, ${fmtDateTime(item.submitted_at, zone.value)}`,
    onKeydown: (e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            emit("open", item.id);
        }
    },
});

const filterParams = () => {
    const p = {};
    if (range.value.from) p.from = range.value.from;
    if (range.value.to) p.to = range.value.to;
    if (vehicle.value.length) p.vehicle = vehicle.value;
    if (driver.value.length) p.driver = driver.value;
    if (status.value.length) p.status = status.value;
    if (defectStatus.value) p.defect_status = defectStatus.value;
    if (type.value) p.type = type.value;
    if (gps.value) p.gps_verified = gps.value;
    if (searchApplied.value) p.q = searchApplied.value;
    p.sort = sortKey.value;
    p.dir = sortDir.value;
    return p;
};

// ---- list ---------------------------------------------------------------------

const rows = ref([]);
const loading = ref(false);
const loadError = ref("");
const lastPage = ref(1);
const totalItems = ref(0);
const totals = ref(null);
// Whether the request the totals came from had no filters, so a zero means the company has none.
const totalsUnfiltered = ref(false);
// The company has no inspections at all: show the explainer instead of an empty table.
const firstRun = computed(() => !loadError.value && !activeFilters.value.length
    && totalsUnfiltered.value && totals.value?.total === 0);

// Guards against an older response landing after a newer one.
let requestSeq = 0;
const fetchRows = () => {
    const seq = ++requestSeq;
    if (customInvalid.value) {
        rows.value = [];
        totals.value = null;
        totalItems.value = 0;
        lastPage.value = 1;
        loading.value = false;
        return;
    }
    loading.value = true;
    loadError.value = "";
    const unfiltered = !activeFilters.value.length;
    send("get", "fleet/inspections", {...filterParams(), page: page.value, per_page: perPage.value})
        .then((r) => {
            if (seq !== requestSeq) return;
            const d = r.data.data ?? {};
            rows.value = d.data ?? [];
            lastPage.value = d.last_page ?? 1;
            totalItems.value = d.total ?? 0;
            totals.value = d.totals ?? null;
            totalsUnfiltered.value = unfiltered;
            zone.value = d.company_timezone ?? null;
            emit("totals", {totals: totals.value, zone: zone.value});
            // A page that emptied steps back.
            if (!rows.value.length && page.value > 1) page.value = Math.max(1, lastPage.value);
        })
        .catch((e) => {
            if (seq !== requestSeq) return;
            loadError.value = apiError(e, "Inspections could not be loaded.");
        })
        .finally(() => {
            if (seq === requestSeq) loading.value = false;
        });
};

// One fetch and one URL write per tick, however many refs changed.
let scheduled = false;
let writing = 0;
const schedule = () => {
    if (scheduled) return;
    scheduled = true;
    nextTick(() => {
        scheduled = false;
        writing++;
        patchQuery(router, myQuery()).finally(() => writing--);
        fetchRows();
    });
};

const fetchVehicles = () => {
    vehiclesLoading.value = true;
    send("get", "assets/list", {include_deleted: 1})
        .then((r) => {
            vehicles.value = vehicleOptions(r?.data?.data);
        })
        .catch(() => {
            vehicles.value = [];
        })
        .finally(() => {
            vehiclesLoading.value = false;
        });
};

const fetchDrivers = () => {
    driversLoading.value = true;
    send("get", "companies/drivers?list=true")
        .then((r) => {
            drivers.value = driverOptions(r?.data?.data);
        })
        .catch(() => {
            drivers.value = [];
        })
        .finally(() => {
            driversLoading.value = false;
        });
};

// ---- export -------------------------------------------------------------------

const exporting = ref(false);
let searchTimer = null;

const exportCsv = async () => {
    if (exportBlocked.value) return;
    // The search as typed, not as last debounced.
    clearTimeout(searchTimer);
    searchApplied.value = (search.value || "").trim().slice(0, 100);
    exporting.value = true;
    const params = {...filterParams(), from: exportRange.value.from, to: exportRange.value.to};
    try {
        const response = await send("get", "fleet/inspections/export", params, {responseType: "text"});
        // Decoding as text drops the byte order mark Excel needs for UTF-8.
        const text = String(response?.data ?? "");
        const data = text.startsWith("﻿") ? text : "﻿" + text;
        handleDownloadResponse({data}, `inspections_${params.from}_to_${params.to}.csv`);
    } catch (e) {
        // A text response: a refusal arrives as the JSON string.
        let data = e?.response?.data;
        try {
            if (typeof data === "string") data = JSON.parse(data);
        } catch {
            data = null;
        }
        showSnackbar(apiError({response: {...e?.response, data}}, "The export could not be made."), "error");
    } finally {
        exporting.value = false;
    }
};

// ---- reacting -------------------------------------------------------------------

watch(search, (v) => {
    clearTimeout(searchTimer);
    searchTimer = setTimeout(() => {
        searchApplied.value = (v || "").trim().slice(0, 100);
    }, 350);
});

const filtersKey = computed(() => JSON.stringify([
    preset.value, customFrom.value, customTo.value, vehicle.value, driver.value, status.value,
    defectStatus.value, type.value, gps.value, searchApplied.value, sortKey.value, sortDir.value, perPage.value,
]));
watch(filtersKey, () => {
    if (!applying) page.value = 1;
    schedule();
});
watch(page, schedule);
// The first load guesses the fleet's zone; when the real one moves "today", reload.
watch(tz, (now, before) => {
    if (preset.value === "custom" || preset.value === "all") return;
    const a = presetRange(preset.value, now);
    const b = presetRange(preset.value, before);
    if (a?.from !== b?.from || a?.to !== b?.to) schedule();
});
// The back button or a link changed this list's part of the URL.
watch(() => route.query, (q) => {
    if (!writing && !sameQuery(q, myQuery())) applyQuery(q);
});

applyQuery(route.query);
onMounted(() => {
    schedule();
    fetchVehicles();
    fetchDrivers();
});
onBeforeUnmount(() => clearTimeout(searchTimer));

defineExpose({refresh: fetchRows});
</script>
